import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Plus, Edit2, Trash2, Play, Pause, GitBranch, BookOpen, Settings, Search, FileText, Upload, X, Loader2, CheckCircle, XCircle, Sparkles, ShieldAlert, RefreshCw, Plug } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import BotFlowModal from "../../components/BotFlowModal";
import BotStepModal from "../../components/BotStepModal";
import BotKnowledgeModal from "../../components/BotKnowledgeModal";
import BotApiToolModal from "../../components/BotApiToolModal";

const TABS = [
  { id: "flows", label: "Flujos", icon: GitBranch },
  { id: "knowledge", label: "Conocimiento", icon: BookOpen },
  { id: "apitools", label: "APIs", icon: Plug },
  { id: "settings", label: "Configuración", icon: Settings },
];

const Bot = () => {
  const [activeTab, setActiveTab] = useState("flows");
  const [flows, setFlows] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [apiTools, setApiTools] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [loading, setLoading] = useState(false);

  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedFlowForStep, setSelectedFlowForStep] = useState(null);
  const [knowledgeModalOpen, setKnowledgeModalOpen] = useState(false);
  const [selectedKnowledge, setSelectedKnowledge] = useState(null);
  const [apiToolModalOpen, setApiToolModalOpen] = useState(false);
  const [selectedApiTool, setSelectedApiTool] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [botSettings, setBotSettings] = useState({});
  const [selectedWhatsapp, setSelectedWhatsapp] = useState(null);
  // Catálogo de proveedores de IA servido por el backend: la UI se pinta a
  // partir de él, así un proveedor nuevo no requiere tocar este archivo.
  const [aiProviders, setAiProviders] = useState([]);
  // Regex (source) de modelos multimodales que envía el backend: sube el badge
  // de "Visión" a ✓ cuando el modelo escrito ve imágenes, aunque el proveedor
  // traiga visión en false (el mismo criterio que aplica el motor al responder).
  const [visionModelPattern, setVisionModelPattern] = useState("");
  const [testingAI, setTestingAI] = useState(false);
  const [aiTestResult, setAiTestResult] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [serverTime, setServerTime] = useState(null);

  useEffect(() => {
    loadWhatsapps();
    loadAIProviders();
  }, []);

  useEffect(() => {
    if (activeTab === "flows") loadFlows();
    if (activeTab === "knowledge") loadKnowledge();
    if (activeTab === "apitools") loadApiTools();
    if (activeTab === "settings") {
      loadSettings();
      loadServerTime();
    }
  }, [activeTab, selectedWhatsapp]);

  const loadWhatsapps = async () => {
    try {
      const { data } = await api.get("/whatsapp/");
      setWhatsapps(data);
      if (data.length > 0 && !selectedWhatsapp) {
        setSelectedWhatsapp(data[0]);
      }
    } catch (err) { toastError(err); }
  };

  const loadAIProviders = async () => {
    try {
      const { data } = await api.get("/bot/ai-providers");
      // Tolerante con la forma antigua (array plano) y la nueva ({ providers, ... }).
      setAiProviders(Array.isArray(data) ? data : data.providers || []);
      if (!Array.isArray(data) && data.visionModelPattern) {
        setVisionModelPattern(data.visionModelPattern);
      }
    } catch (err) { toastError(err); }
  };

  const loadFlows = async () => {
    setLoading(true);
    try {
      const params = selectedWhatsapp ? { whatsappId: selectedWhatsapp.id } : {};
      const { data } = await api.get("/bot/flows", { params });
      setFlows(data);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const loadKnowledge = async () => {
    setLoading(true);
    try {
      const params = selectedWhatsapp ? { whatsappId: selectedWhatsapp.id } : {};
      const { data } = await api.get("/bot/knowledge", { params });
      setKnowledge(data);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const loadApiTools = async () => {
    setLoading(true);
    try {
      const params = selectedWhatsapp ? { whatsappId: selectedWhatsapp.id } : {};
      const { data } = await api.get("/bot/api-tools", { params });
      setApiTools(data);
    } catch (err) { toastError(err); }
    setLoading(false);
  };

  const loadSettings = async () => {
    if (!selectedWhatsapp) return;
    try {
      const { data } = await api.get(`/whatsapp/${selectedWhatsapp.id}`);
      setBotSettings({
        botEnabled: data.botEnabled || false,
        aiEnabled: data.aiEnabled || false,
        aiFallbackToHuman: data.aiFallbackToHuman !== false,
        aiUpdateContact: data.aiUpdateContact || false,
        aiCanBookAppointments: data.aiCanBookAppointments || false,
        aiCanUseApiTools: data.aiCanUseApiTools || false,
        enableAudioTranscription: data.enableAudioTranscription || false,
        scheduleEnabled: data.scheduleEnabled || false,
        scheduleStart: data.scheduleStart || "09:00",
        scheduleEnd: data.scheduleEnd || "18:00",
        scheduleTimezone: data.scheduleTimezone || "America/Santo_Domingo",
        scheduleDays: data.scheduleDays || [1, 2, 3, 4, 5],
        scheduleOverrides: data.scheduleOverrides || {},
        outsideHoursMessage: data.outsideHoursMessage || "",
        aiProvider: data.aiProvider || "gemini",
        // Respaldo en las columnas antiguas: conexiones configuradas antes de
        // que existiera el selector de proveedor siguen mostrando sus datos.
        aiApiKey: data.aiApiKey || data.geminiApiKey || "",
        aiBaseUrl: data.aiBaseUrl || "",
        aiModel: data.aiModel || "",
        aiEmbeddingModel: data.aiEmbeddingModel || "",
        aiTranscriptionModel: data.aiTranscriptionModel || "",
        aiSystemPrompt: data.aiSystemPrompt || data.geminiSystemPrompt || "",
        aiNegativePrompt: data.aiNegativePrompt || "",
      });
      setAiTestResult(null);
    } catch (err) { toastError(err); }
  };

  const handleSaveSettings = async () => {
    if (!selectedWhatsapp) return;
    try {
      await api.put(`/whatsapp/${selectedWhatsapp.id}`, botSettings);
      toast.success("Configuración guardada");
    } catch (err) { toastError(err); }
  };

  const currentProvider =
    aiProviders.find(p => p.id === (botSettings.aiProvider || "gemini")) || null;

  // Al cambiar de proveedor se reponen los valores por defecto del catálogo:
  // dejar el modelo del proveedor anterior sería un error garantizado.
  const handleProviderChange = (providerId) => {
    const entry = aiProviders.find(p => p.id === providerId);
    setAiTestResult(null);
    setBotSettings(prev => ({
      ...prev,
      aiProvider: providerId,
      aiBaseUrl: entry?.defaultBaseUrl || "",
      aiModel: entry?.defaultModel || "",
      aiEmbeddingModel: entry?.defaultEmbeddingModel || "",
      aiTranscriptionModel: entry?.defaultTranscriptionModel || "",
    }));
  };

  const handleTestAI = async () => {
    if (currentProvider?.requiresApiKey && !botSettings.aiApiKey) {
      toast.warn(`Ingresa la API key de ${currentProvider.label}`);
      return;
    }
    setTestingAI(true);
    setAiTestResult(null);
    try {
      const { data } = await api.post("/bot/test-ai", {
        whatsappId: selectedWhatsapp?.id,
        aiProvider: botSettings.aiProvider || "gemini",
        aiApiKey: botSettings.aiApiKey,
        aiBaseUrl: botSettings.aiBaseUrl,
        aiModel: botSettings.aiModel,
        aiEmbeddingModel: botSettings.aiEmbeddingModel,
        aiTranscriptionModel: botSettings.aiTranscriptionModel,
      });
      setAiTestResult(data);
    } catch (err) { toastError(err); }
    setTestingAI(false);
  };

  // Los vectores del proveedor anterior tienen otra dimensión y la búsqueda
  // los ignora: hay que regenerarlos tras cambiar de modelo de embeddings.
  const handleReindexKnowledge = async () => {
    if (!selectedWhatsapp) return;
    setReindexing(true);
    try {
      const { data } = await api.post("/bot/knowledge/reindex", {
        whatsappId: selectedWhatsapp.id,
      });
      toast.success(
        data.total > 0
          ? `Reindexando ${data.total} documento(s) en segundo plano`
          : "No hay conocimiento para reindexar"
      );
    } catch (err) { toastError(err); }
    setReindexing(false);
  };

  const loadServerTime = async () => {
    try {
      const { data } = await api.get("/bot/server-time");
      setServerTime(data);
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try {
      if (deleteType === "flow") {
        await api.delete(`/bot/flows/${deleteTarget.id}`);
        toast.success("Flujo eliminado");
        loadFlows();
      } else if (deleteType === "knowledge") {
        await api.delete(`/bot/knowledge/${deleteTarget.id}`);
        toast.success("Conocimiento eliminado");
        loadKnowledge();
      } else if (deleteType === "apitool") {
        await api.delete(`/bot/api-tools/${deleteTarget.id}`);
        toast.success("API eliminada");
        loadApiTools();
      }
    } catch (err) { toastError(err); }
    setConfirmOpen(false);
    setDeleteTarget(null);
    setDeleteType(null);
  };

  const handleToggleFlow = async (flow) => {
    try {
      await api.put(`/bot/flows/${flow.id}`, { isActive: !flow.isActive });
      loadFlows();
    } catch (err) { toastError(err); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", file.name);
    if (selectedWhatsapp) formData.append("whatsappId", selectedWhatsapp.id);

    try {
      await api.post("/bot/knowledge/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento cargado");
      loadKnowledge();
    } catch (err) { toastError(err); }
    e.target.value = "";
  };

  const updateSetting = (key, value) => {
    setBotSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleDay = (day) => {
    const days = botSettings.scheduleDays || [];
    if (days.includes(day)) {
      updateSetting("scheduleDays", days.filter(d => d !== day));
    } else {
      updateSetting("scheduleDays", [...days, day].sort());
    }
  };

  const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleteTarget ? `¿Eliminar "${deleteTarget.name || deleteTarget.title}"?` : ""}
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
      >
        Esta acción no se puede deshacer.
      </ConfirmationModal>

      <BotFlowModal
        open={flowModalOpen}
        onClose={() => { setFlowModalOpen(false); setSelectedFlow(null); }}
        flow={selectedFlow}
        whatsappId={selectedWhatsapp?.id}
        onSave={loadFlows}
      />

      <BotStepModal
        open={stepModalOpen}
        onClose={() => { setStepModalOpen(false); setSelectedStep(null); setSelectedFlowForStep(null); }}
        step={selectedStep}
        flowId={selectedFlowForStep?.id}
        onSave={loadFlows}
      />

      <BotKnowledgeModal
        open={knowledgeModalOpen}
        onClose={() => { setKnowledgeModalOpen(false); setSelectedKnowledge(null); }}
        knowledge={selectedKnowledge}
        whatsappId={selectedWhatsapp?.id}
        onSave={loadKnowledge}
      />

      <BotApiToolModal
        open={apiToolModalOpen}
        onClose={() => { setApiToolModalOpen(false); setSelectedApiTool(null); }}
        tool={selectedApiTool}
        whatsappId={selectedWhatsapp?.id}
        onSave={loadApiTools}
      />

      <MainHeader>
        <Title>Bot de WhatsApp</Title>
        <MainHeaderButtonsWrapper>
          <select
            value={selectedWhatsapp?.id || ""}
            onChange={(e) => {
              const wa = whatsapps.find(w => w.id === Number(e.target.value));
              setSelectedWhatsapp(wa);
            }}
            className="px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg"
          >
            {whatsapps.map(wa => (
              <option key={wa.id} value={wa.id}>{wa.name}</option>
            ))}
          </select>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <div className="px-6">
        <div className="flex gap-1 border-b border-slate-200 dark:border-slate-800 mb-4">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {activeTab === "flows" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">
                {flows.length} flujo{flows.length !== 1 && "s"}
              </p>
              <button
                onClick={() => { setSelectedFlow(null); setFlowModalOpen(true); }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" /> Nuevo Flujo
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : flows.length === 0 ? (
              <div className="text-center py-16">
                <GitBranch className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-500">No hay flujos</p>
                <p className="text-sm text-slate-400 mt-1">Crea tu primer flujo de conversación</p>
              </div>
            ) : (
              <div className="space-y-3">
                {flows.map(flow => (
                  <div key={flow.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleFlow(flow)}
                          className={`p-1.5 rounded-lg transition-colors ${
                            flow.isActive
                              ? "text-green-600 bg-green-50 dark:bg-green-900/30"
                              : "text-slate-400 bg-slate-100 dark:bg-slate-800"
                          }`}
                        >
                          {flow.isActive ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                        </button>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{flow.name}</p>
                          <p className="text-xs text-slate-400">
                            {flow.triggerType === "keyword" && `Palabra clave: "${flow.triggerValue}"`}
                            {flow.triggerType === "all_new" && "Todos los mensajes nuevos"}
                            {flow.triggerType === "queue" && `Cola: ${flow.triggerValue}`}
                            {" · "}{flow.steps?.length || 0} pasos
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setSelectedFlowForStep(flow); setSelectedStep(null); setStepModalOpen(true); }}
                          title="Agregar paso"
                          className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 dark:hover:bg-green-950/30 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedFlow(flow); setFlowModalOpen(true); }}
                          title="Editar flujo"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeleteType("flow"); setDeleteTarget(flow); setConfirmOpen(true); }}
                          title="Eliminar flujo"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {flow.steps && flow.steps.length > 0 && (
                      <div className="border-t border-slate-100 dark:border-slate-800 px-4 py-2">
                        <div className="flex flex-wrap gap-2">
                          {flow.steps.sort((a, b) => a.stepOrder - b.stepOrder).map((step, idx) => (
                            <div
                              key={step.id}
                              className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                              onClick={() => { setSelectedFlowForStep(flow); setSelectedStep(step); setStepModalOpen(true); }}
                            >
                              <span className="w-5 h-5 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full text-[10px] font-bold">
                                {step.stepOrder}
                              </span>
                              <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded text-[10px] font-mono">
                                ID:{step.id}
                              </span>
                              <span className="text-slate-600 dark:text-slate-400 truncate max-w-[150px]">
                                {step.message?.substring(0, 25) || step.stepType}
                              </span>
                              <span className="text-slate-300 dark:text-slate-600">
                                {step.stepType === "options" && "📋"}
                                {step.stepType === "question" && "❓"}
                                {step.stepType === "end" && "🏁"}
                                {step.stepType === "message" && "💬"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "knowledge" && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">
                {knowledge.length} elemento{knowledge.length !== 1 && "s"}
              </p>
              <div className="flex gap-2">
                <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer">
                  <Upload className="w-4 h-4" />
                  Subir Documento
                  <input
                    type="file"
                    accept=".pdf,.docx,.txt,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
                <button
                  onClick={() => { setSelectedKnowledge(null); setKnowledgeModalOpen(true); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" /> Agregar Texto
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : knowledge.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-500">Sin conocimiento</p>
                <p className="text-sm text-slate-400 mt-1">Agrega documentos o textos para que la IA aprenda</p>
              </div>
            ) : (
              <div className="space-y-3">
                {knowledge.map(item => (
                  <div key={item.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          item.sourceType === "document" ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600" :
                          item.sourceType === "url" ? "bg-purple-50 dark:bg-purple-900/30 text-purple-600" :
                          "bg-green-50 dark:bg-green-900/30 text-green-600"
                        }`}>
                          <FileText className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{item.title}</p>
                          <p className="text-xs text-slate-400">
                            {item.sourceType === "document" ? `📄 ${item.sourceFile}` : "✏️ Texto"}
                            {" · "}{item.content?.length || 0} caracteres
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setSelectedKnowledge(item); setKnowledgeModalOpen(true); }}
                          title="Editar"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeleteType("knowledge"); setDeleteTarget(item); setConfirmOpen(true); }}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "apitools" && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  APIs que la IA puede consultar en vivo. Actívalas con el toggle <b>“La IA puede consultar APIs”</b> en Configuración.
                </p>
              </div>
              <button
                onClick={() => { setSelectedApiTool(null); setApiToolModalOpen(true); }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm flex-shrink-0"
              >
                <Plus className="w-4 h-4" /> Agregar API
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
              </div>
            ) : apiTools.length === 0 ? (
              <div className="text-center py-16">
                <Plug className="w-12 h-12 text-slate-200 dark:text-slate-700 mx-auto mb-3" />
                <p className="text-base font-semibold text-slate-500">Sin APIs</p>
                <p className="text-sm text-slate-400 mt-1">Agrega un endpoint para que la IA consulte datos reales</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apiTools.map(item => (
                  <div key={item.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`p-2 rounded-lg ${item.isActive ? "bg-green-50 dark:bg-green-900/30 text-green-600" : "bg-slate-100 dark:bg-slate-800 text-slate-400"}`}>
                          <Plug className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                            {item.name}
                            <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{item.method}</span>
                            {item.hasToken && (
                              <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/30 text-amber-600">🔑 token</span>
                            )}
                            {item.whatsappId == null && (
                              <span className="ml-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-600">global</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{item.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => { setSelectedApiTool(item); setApiToolModalOpen(true); }}
                          title="Editar"
                          className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => { setDeleteType("apitool"); setDeleteTarget(item); setConfirmOpen(true); }}
                          title="Eliminar"
                          className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <div className="max-w-2xl">
            {!selectedWhatsapp ? (
              <p className="text-sm text-slate-500">Selecciona una conexión de WhatsApp</p>
            ) : (
              <div className="space-y-6">
                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-indigo-600" /> Bot General
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Bot habilitado</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("botEnabled", !botSettings.botEnabled)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.botEnabled ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.botEnabled ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" /> Inteligencia Artificial
                    {currentProvider && (
                      <span className="text-xs font-normal text-slate-400">
                        · {currentProvider.label}
                      </span>
                    )}
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">IA habilitada</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("aiEnabled", !botSettings.aiEnabled)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.aiEnabled ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.aiEnabled ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Fallback a humano</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("aiFallbackToHuman", !botSettings.aiFallbackToHuman)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.aiFallbackToHuman ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.aiFallbackToHuman ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">La IA registra datos de contacto</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("aiUpdateContact", !botSettings.aiUpdateContact)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.aiUpdateContact ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.aiUpdateContact ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    {botSettings.aiUpdateContact && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
                        Cuando el cliente confirme su nombre, correo u otro dato durante la conversación, la IA lo guardará en su ficha de contacto automáticamente.
                      </p>
                    )}
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">La IA puede agendar citas</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("aiCanBookAppointments", !botSettings.aiCanBookAppointments)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.aiCanBookAppointments ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.aiCanBookAppointments ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    {botSettings.aiCanBookAppointments && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
                        La IA ofrecerá horarios reales de Citas y agendará directamente cuando el cliente confirme motivo, horario y (si aplica) con quién.
                      </p>
                    )}
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">La IA puede consultar APIs</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("aiCanUseApiTools", !botSettings.aiCanUseApiTools)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.aiCanUseApiTools ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.aiCanUseApiTools ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    {botSettings.aiCanUseApiTools && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 -mt-2">
                        La IA podrá consultar en vivo las APIs que definas en la pestaña <b>APIs</b> (estado de pedidos, saldos, disponibilidad…) para responder con datos reales.
                      </p>
                    )}
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Transcribir Audios (IA)</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("enableAudioTranscription", !botSettings.enableAudioTranscription)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.enableAudioTranscription ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.enableAudioTranscription ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    {botSettings.enableAudioTranscription && currentProvider && !currentProvider.capabilities.audio && (
                      <p className="text-xs text-amber-600 dark:text-amber-500 -mt-2">
                        {currentProvider.label} no transcribe audio: los mensajes de voz se recibirán sin transcripción.
                      </p>
                    )}

                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Proveedor de IA</label>
                      <select
                        value={botSettings.aiProvider || "gemini"}
                        onChange={(e) => handleProviderChange(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      >
                        {aiProviders.map((p) => (
                          <option key={p.id} value={p.id}>{p.label}</option>
                        ))}
                      </select>
                      {currentProvider && (() => {
                        // La visión se decide por MODELO, no solo por proveedor:
                        // un gateway (OpenCode, OpenRouter...) sirve modelos de
                        // texto y multimodales bajo el mismo id. Se cruza la
                        // bandera del catálogo con el patrón que manda el backend
                        // contra el modelo escrito, igual que hace el motor.
                        const modelHasVision =
                          !!visionModelPattern &&
                          new RegExp(visionModelPattern, "i").test(
                            (botSettings.aiModel || currentProvider.defaultModel || "").trim()
                          );
                        const has = (key) =>
                          key === "vision"
                            ? currentProvider.capabilities.vision || modelHasVision
                            : currentProvider.capabilities[key];
                        return (
                          <div className="flex flex-wrap items-center gap-1.5 mt-2">
                            {[
                              ["Visión", "vision"],
                              ["Documentos", "documents"],
                              ["Embeddings", "embeddings"],
                              // Sin endpoint tipo Whisper el audio va al propio
                              // modelo, así que solo funciona si ese lo acepta.
                              [currentProvider.transcriptionMode === "chat" ? "Audio (según modelo)" : "Audio", "audio"],
                            ].map(([label, key]) => (
                              <span
                                key={key}
                                className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                  has(key)
                                    ? "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400"
                                    : "bg-slate-100 dark:bg-slate-800 text-slate-400"
                                }`}
                              >
                                {has(key) ? "✓" : "—"} {label}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      {currentProvider?.hint && (
                        <p className="text-xs text-slate-400 mt-1.5">{currentProvider.hint}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Modelo</label>
                      <input
                        type="text"
                        list="ai-model-suggestions"
                        value={botSettings.aiModel || ""}
                        onChange={(e) => updateSetting("aiModel", e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        placeholder={currentProvider?.defaultModel || "nombre-del-modelo"}
                      />
                      <datalist id="ai-model-suggestions">
                        {(currentProvider?.models || []).map((m) => (
                          <option key={m} value={m} />
                        ))}
                      </datalist>
                      <p className="text-xs text-slate-400 mt-1">
                        Acepta cualquier nombre de modelo del proveedor, no solo las sugerencias.
                      </p>
                    </div>

                    {currentProvider?.baseUrlEditable && (
                      <div>
                        <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
                          URL base {currentProvider.defaultBaseUrl ? "(opcional)" : ""}
                        </label>
                        <input
                          type="text"
                          value={botSettings.aiBaseUrl || ""}
                          onChange={(e) => updateSetting("aiBaseUrl", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          placeholder={currentProvider.defaultBaseUrl || "https://mi-servidor/v1"}
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">
                        API Key {currentProvider && !currentProvider.requiresApiKey ? "(opcional)" : ""}
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          value={botSettings.aiApiKey || ""}
                          onChange={(e) => updateSetting("aiApiKey", e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                          placeholder={currentProvider?.apiKeyPlaceholder || "sk-..."}
                        />
                        <button
                          onClick={handleTestAI}
                          disabled={testingAI}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          {testingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          Probar
                        </button>
                      </div>
                      {currentProvider?.docsUrl && (
                        <a
                          href={currentProvider.docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block text-xs text-indigo-600 hover:underline mt-1"
                        >
                          Obtener una API key de {currentProvider.label}
                        </a>
                      )}
                      {aiTestResult && (
                        <p className={`text-xs mt-1 ${aiTestResult.success ? "text-green-600" : "text-red-500"}`}>
                          {aiTestResult.success ? `✓ Conexión exitosa: ${aiTestResult.message}` : `✗ Error: ${aiTestResult.message}`}
                        </p>
                      )}
                    </div>

                    {(currentProvider?.capabilities?.embeddings || currentProvider?.capabilities?.audio) && (
                      <div className="grid grid-cols-2 gap-4">
                        {currentProvider.capabilities.embeddings && (
                          <div>
                            <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Modelo de embeddings</label>
                            <input
                              type="text"
                              value={botSettings.aiEmbeddingModel || ""}
                              onChange={(e) => updateSetting("aiEmbeddingModel", e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder={currentProvider.defaultEmbeddingModel || "(sin búsqueda vectorial)"}
                            />
                          </div>
                        )}
                        {currentProvider.capabilities.audio && (
                          <div>
                            <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Modelo de transcripción</label>
                            <input
                              type="text"
                              value={botSettings.aiTranscriptionModel || ""}
                              onChange={(e) => updateSetting("aiTranscriptionModel", e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                              placeholder={currentProvider.defaultTranscriptionModel || "whisper-1"}
                            />
                            {currentProvider.transcriptionMode === "chat" && (
                              <p className="text-xs text-slate-400 mt-1">
                                Debe ser un modelo que acepte audio de entrada.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Prompt del sistema</label>
                      <textarea
                        value={botSettings.aiSystemPrompt || ""}
                        onChange={(e) => updateSetting("aiSystemPrompt", e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
                        placeholder="Eres un asistente virtual de atención al cliente..."
                      />
                      <p className="text-xs text-slate-400 mt-1">Instrucciones para la IA. Se combina con el conocimiento guardado.</p>
                    </div>

                    <div>
                      <label className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300 mb-1">
                        <ShieldAlert className="w-4 h-4 text-red-500" /> Prompt negativo (lo que NO debe decir)
                      </label>
                      <textarea
                        value={botSettings.aiNegativePrompt || ""}
                        onChange={(e) => updateSetting("aiNegativePrompt", e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
                        placeholder={"- No prometas descuentos, precios ni plazos de entrega.\n- No hables de la competencia.\n- No des consejos médicos, legales ni financieros.\n- No compartas datos internos de la empresa."}
                      />
                      <p className="text-xs text-slate-400 mt-1">
                        Una restricción por línea. Tiene prioridad sobre el prompt del sistema y sobre el conocimiento, aunque el cliente insista.
                      </p>
                    </div>

                    {currentProvider?.capabilities?.embeddings && (
                      <div className="flex items-center justify-between pt-1">
                        <p className="text-xs text-slate-400 pr-3">
                          Al cambiar de proveedor o de modelo de embeddings, reindexa el conocimiento para volver a usar la búsqueda vectorial.
                        </p>
                        <button
                          onClick={handleReindexKnowledge}
                          disabled={reindexing}
                          className="flex items-center gap-1.5 shrink-0 px-3 py-2 text-sm font-medium border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          {reindexing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          Reindexar
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                    <Settings className="w-4 h-4 text-amber-600" /> Horario de Servicio
                  </h3>
                  <div className="space-y-4">
                    <label className="flex items-center justify-between">
                      <span className="text-sm text-slate-700 dark:text-slate-300">Horario habilitado</span>
                      <button
                        type="button"
                        onClick={() => updateSetting("scheduleEnabled", !botSettings.scheduleEnabled)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${
                          botSettings.scheduleEnabled ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          botSettings.scheduleEnabled ? "translate-x-5" : ""
                        }`} />
                      </button>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Hora inicio (general)</label>
                        <input
                          type="time"
                          value={botSettings.scheduleStart || "09:00"}
                          onChange={(e) => updateSetting("scheduleStart", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Hora fin (general)</label>
                        <input
                          type="time"
                          value={botSettings.scheduleEnd || "18:00"}
                          onChange={(e) => updateSetting("scheduleEnd", e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Zona horaria</label>
                      <input
                        type="text"
                        value={botSettings.scheduleTimezone || "America/Santo_Domingo"}
                        onChange={(e) => updateSetting("scheduleTimezone", e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                      />
                      {serverTime && (
                        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                          Hora del servidor: {new Date(serverTime.serverTime).toLocaleString()} ({serverTime.timezone})
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-2">Días activos (clic para activar/desactivar)</label>
                      <div className="flex gap-2">
                        {dayNames.map((name, idx) => {
                          const day = idx + 1;
                          const active = (botSettings.scheduleDays || []).includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleDay(day)}
                              className={`w-10 h-10 rounded-lg text-xs font-semibold transition-colors ${
                                active
                                  ? "bg-indigo-600 text-white"
                                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
                              }`}
                            >
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-2">Horarios por día (opcional)</label>
                      <p className="text-xs text-slate-400 mb-3">Configura horarios diferentes para días específicos. Si no se configura, se usa el horario general.</p>
                      <div className="space-y-2">
                        {dayNames.map((name, idx) => {
                          const day = idx + 1;
                          const isActive = (botSettings.scheduleDays || []).includes(day);
                          const overrides = botSettings.scheduleOverrides || {};
                          const override = overrides[String(day)] || {};
                          const isClosed = override.closed || false;
                          return (
                            <div key={day} className={`flex items-center gap-3 p-2 rounded-lg ${isActive ? 'bg-slate-50 dark:bg-slate-800/50' : 'opacity-50'}`}>
                              <span className="w-10 text-xs font-semibold text-slate-600 dark:text-slate-400">{name}</span>
                              <label className="flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={isClosed}
                                  disabled={!isActive}
                                  onChange={(e) => {
                                    const newOverrides = { ...overrides };
                                    if (e.target.checked) {
                                      newOverrides[String(day)] = { closed: true, start: "00:00", end: "00:00" };
                                    } else {
                                      delete newOverrides[String(day)];
                                    }
                                    updateSetting("scheduleOverrides", newOverrides);
                                  }}
                                  className="rounded"
                                />
                                <span className="text-xs text-slate-500">Cerrado</span>
                              </label>
                              {!isClosed && (
                                <>
                                  <input
                                    type="time"
                                    value={override.start || ""}
                                    disabled={!isActive}
                                    onChange={(e) => {
                                      const newOverrides = { ...overrides };
                                      newOverrides[String(day)] = { ...newOverrides[String(day)], start: e.target.value, closed: false };
                                      updateSetting("scheduleOverrides", newOverrides);
                                    }}
                                    placeholder={botSettings.scheduleStart || "09:00"}
                                    className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 w-24"
                                  />
                                  <span className="text-xs text-slate-400">a</span>
                                  <input
                                    type="time"
                                    value={override.end || ""}
                                    disabled={!isActive}
                                    onChange={(e) => {
                                      const newOverrides = { ...overrides };
                                      newOverrides[String(day)] = { ...newOverrides[String(day)], end: e.target.value, closed: false };
                                      updateSetting("scheduleOverrides", newOverrides);
                                    }}
                                    placeholder={botSettings.scheduleEnd || "18:00"}
                                    className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 w-24"
                                  />
                                </>
                              )}
                              {isClosed && (
                                <span className="text-xs text-red-500 font-medium">Cerrado todo el día</span>
                              )}
                              {isActive && !override.start && !override.end && !isClosed && (
                                <span className="text-xs text-slate-400 italic">Usa horario general ({botSettings.scheduleStart || "09:00"} - {botSettings.scheduleEnd || "18:00"})</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-700 dark:text-slate-300 mb-1">Mensaje fuera de horario</label>
                      <textarea
                        value={botSettings.outsideHoursMessage || ""}
                        onChange={(e) => updateSetting("outsideHoursMessage", e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
                        placeholder="Lo siento, nuestro horario de atención es de lunes a viernes de 9:00 a 18:00."
                      />
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSaveSettings}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
                >
                  <CheckCircle className="w-4 h-4" /> Guardar Configuración
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default Bot;
